import React from 'react';

/**
 * Reusable Error Fallback Component
 * Can be customized for different error scenarios
 */
export const ErrorFallback = ({ 
  error, 
  resetErrorBoundary,
  title = 'Something went wrong',
  message = 'An unexpected error occurred. Please try again.',
}) => {
  return (
    <div style={{
      padding: '2rem',
      margin: '2rem auto',
      maxWidth: '600px',
      textAlign: 'center',
      backgroundColor: '#fff3cd',
      border: '1px solid #ffc107',
      borderRadius: '8px',
    }}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
      <h2 style={{ color: '#856404', marginBottom: '1rem' }}>{title}</h2>
      <p style={{ color: '#856404', marginBottom: '1.5rem' }}>{message}</p>
      
      {process.env.NODE_ENV === 'development' && error && (
        <details style={{ 
          textAlign: 'left', 
          backgroundColor: '#fff', 
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '1rem',
        }}>
          <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            Error Details (Development Only)
          </summary>
          <pre style={{ 
            fontSize: '0.875rem', 
            overflow: 'auto',
            color: '#d32f2f',
            whiteSpace: 'pre-wrap',
          }}>
            {error.toString()}
          </pre>
        </details>
      )}

      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
        {resetErrorBoundary && (
          <button
            onClick={resetErrorBoundary}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
            }}
          >
            Try Again
          </button>
        )}
        <button
          onClick={() => window.location.href = '/'}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '500',
          }}
        >
          Go to Homepage
        </button>
      </div>
    </div>
  );
};

/**
 * Page-level Error Fallback
 * For errors in entire pages
 */
export const PageErrorFallback = ({ error, resetErrorBoundary }) => {
  return (
    <ErrorFallback
      error={error}
      resetErrorBoundary={resetErrorBoundary}
      title="Page Failed to Load"
      message="We couldn't load this page. Please try refreshing or go back to the homepage."
    />
  );
};

/**
 * Component-level Error Fallback
 * For errors in specific components
 */
export const ComponentErrorFallback = ({ error, componentName }) => {
  return (
    <div style={{
      padding: '1rem',
      margin: '1rem 0',
      backgroundColor: '#f8d7da',
      border: '1px solid #f5c6cb',
      borderRadius: '4px',
      color: '#721c24',
    }}>
      <strong>⚠️ {componentName || 'Component'} Error</strong>
      <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
        This section couldn't be loaded. Please refresh the page.
      </p>
      {process.env.NODE_ENV === 'development' && error && (
        <details style={{ marginTop: '0.5rem' }}>
          <summary style={{ cursor: 'pointer', fontSize: '0.875rem' }}>
            Error Details
          </summary>
          <pre style={{ 
            fontSize: '0.75rem', 
            overflow: 'auto',
            marginTop: '0.5rem',
            padding: '0.5rem',
            backgroundColor: '#fff',
            borderRadius: '4px',
          }}>
            {error.toString()}
          </pre>
        </details>
      )}
    </div>
  );
};

export default ErrorFallback;
